import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';

export const jobFiltersRouteMappings = {
  company: 'company.name',
};

export const searchClient = instantMeiliSearch(
  process.env.MIX_MEILISEARCH_HOST || '',
  process.env.MIX_MEILISEARCH_SEARCH_KEY || '',
  { primaryKey: 'id' },
);

export const softDeletedFilter = '(__soft_deleted = 0)';

export const statesWithAbbr = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};
export const states = Object.values(statesWithAbbr);
export const statesToLowerCase = states.map(state => state.toLowerCase());

export function isStateSearch(query) {
  return (
    isStateAbbrSearch(query) || statesToLowerCase.includes(query.toLowerCase())
  );
}

export function isStateAbbrSearch(query) {
  return Boolean(query.length === 2 && statesWithAbbr[query.toUpperCase()]);
}

export function isZipCodeSearch(query) {
  return query.length === 5 && !isNaN(parseInt(query));
}

export function storeZipSearchResult(query, hit) {
  sessionStorage.setItem(
    'zips_city_state',
    JSON.stringify({
      [query]: hit.code === query ? hit.city_state : query,
    }),
  );
}

export function isEqual(newValue, initialValue) {
  return Array.isArray(newValue)
    ? initialValue && newValue.every(value => initialValue.includes(value))
    : newValue === initialValue;
}

export const debounced = debouncePromise(items => Promise.resolve(items), 500);

function debouncePromise(fn, time) {
  let timerId;

  return function debounced(...args) {
    if (timerId) {
      clearTimeout(timerId);
    }

    return new Promise(resolve => {
      timerId = setTimeout(() => resolve(fn(...args)), time);
    });
  };
}

export function formatNumber(value) {
  return Number(value).toLocaleString();
}

export function getFacets(params) {
  return params['job_filters'].map(
    filter => jobFiltersRouteMappings[filter] ?? filter,
  );
}

export function getLocationSlug(name) {
  return slugify(name.split(' ').map(encodeURIComponent).join('-'));
}
export function getLocationName(slug) {
  return slug.split('-').filter(Boolean).map(decodeURIComponent).join(' ');
}

export function titleCase(words) {
  return words
    .split(' ')
    .filter(Boolean)
    .map(word => word.replace(word[0], word[0].toUpperCase()))
    .join(' ');
}

export async function getBreadcrumbs(query) {
  const { breadcrumbs } = await buildFiltersAndBreadcrumbs(query);
  return breadcrumbs;
}

export async function buildFiltersString(query) {
  const { filters } = await buildFiltersAndBreadcrumbs(query);
  return filters;
}

export function checkAndReplaceStateAbbrWithFullStateName(query) {
  return query.replace(/\b([A-Z]{2})\b$/gi, match => {
    return statesWithAbbr[match.toUpperCase()] || match;
  });
}

export async function buildFiltersAndBreadcrumbs(query) {
  let { city: cityQuery, page, state } = getUrlParameters();
  query = (query ?? '').trim();
  const filters = [];
  const conjuctiveFilters = [];
  const breadcrumbs = [`All ${titleCase(page)}`];

  if (query) {
    query = checkAndReplaceStateAbbrWithFullStateName(query);
    query = titleCase(query);
    ({ cityQuery, state } = await getCityQueryAndState(query));

    if (state) {
      state = titleCase(state);
      breadcrumbs.push(state);
      filters.push(`states = '${state}'`);
    }

    if (isZipCodeSearch(query)) {
      breadcrumbs.push(cityQuery);
      breadcrumbs.push(query);
      conjuctiveFilters.push(`(NOT excluded_locations = '${query}')`);
      filters.push(...buildAttributesFilters('zip_codes', query));
    } else {
      if (cityQuery) {
        query = slugify(query);
        cityQuery = titleCase(cityQuery);
        conjuctiveFilters.push(`(NOT excluded_locations = '${query}')`);
        breadcrumbs.push(cityQuery);
        filters.push(...buildAttributesFilters('cities', query));
      } else {
        state = titleCase(state);
        filters.push(`zips_state = '${state}'`);
      }
    }
  }

  if (filters.length) {
    conjuctiveFilters.push(`(${filters.join(' OR ')})`);
  }
  return { filters: conjuctiveFilters.join(' AND '), breadcrumbs };
}

export async function getCityQueryAndState(query = '') {
  const [stateQuery, cityQuery] = await getStateAndCityQueries(query);
  return { cityQuery, state: await findStateInQuery(stateQuery) };
}

export async function getStateAndCityQueries(query) {
  if (isZipCodeSearch(query)) {
    const zipsCityState = JSON.parse(sessionStorage.getItem('zips_city_state'));
    if (!zipsCityState) {
      const data = await getDataUsingPostalCode(query);
      query = data ? data[0]['city_state'] : query;
    } else {
      query = zipsCityState[query] ? zipsCityState[query] : query;
    }
  }
  return query
    ? query
      .split(',')
      .map(_query => _query.trim())
      .reverse()
    : query;
}

export function getCityStateUsingSplitQuery(query) {
  if (!query || isZipCodeSearch(query)) return '';

  const parts = query.split(',').map(_query => _query.trim());

  if (parts.length === 2) {
    return {
      state: slugify(parts[1]),
      city: slugify(parts[0]),
    };
  } else if (parts.length === 1) {
    return {
      state: slugify(parts[0]),
      city: '',
    };
  }

  return '';
}

export async function findStateInQuery(stateQuery) {
  stateQuery = stateQuery.toLowerCase();
  const statesInQuery = states.filter(state =>
    state.toLowerCase() === stateQuery
  );
  return statesInQuery.length ? statesInQuery[0] : '';
}

function buildAttributesFilters(attribute, query) {
  const filters = [];
  const attributesChunks = parseInt(
    process.env.MIX_MEILISEARCH_ATTRIBUTES_CHUNKS || '4',
  );

  for (let i = 1; i <= attributesChunks; i++) {
    filters.push(`${attribute}_${i} = '${query}'`);
  }

  return filters;
}
export function buildUiState(uiState, indexName, query) {
  query = (query ?? '').trim();
  uiState[indexName].query = query;

  return {
    ...uiState,
    [indexName]: createNewUiState(uiState[indexName]),
  };
}

function createNewUiState(partialState) {
  const { city, state } =
    partialState.city && partialState.state
      ? partialState
      : getCityAndStateFromUrl();

  return {
    ...partialState,
    ...{
      configure: {
        ...partialState['configure'],
        filters: buildFiltersString(
          partialState.query || getCityAndStateTitle(city, state),
        ),
      },
    },
  };
}

export function getCityAndStateTitle(city, state) {
  return city && state ? titleCase(`${city}, ${state}`) : '';
}

export function getCityAndStateFromUrl() {
  let city = '';
  let state = '';

  // const pathnameMatches = location.pathname.match(/locations\/(.*?)\/?$/);
  const pathnameSplit = location.pathname.split('/').filter(Boolean);
  if (pathnameSplit[0] === 'locations') {
    state = getLocationName(pathnameSplit?.[1] || '');
    city = getLocationName(pathnameSplit?.[2] || '');
  }

  return { city, state };
}

export const parseURLToState = (queryParameters = undefined, params) => {
  if (!queryParameters) {
    queryParameters = {};
    const searchParams = new URL(location).searchParams;
    searchParams.forEach((value, key) => {
      queryParameters[key] = searchParams.getAll(key);
    });
  }

  const { page } = getUrlParameters();
  const filtersQueryValues = {};

  if (page !== 'companies') {
    params['job_filters'].forEach(filter => {
      filtersQueryValues[jobFiltersRouteMappings[filter] ?? filter] = (
        Array.isArray(queryParameters[filter])
          ? queryParameters[filter]
          : [queryParameters[filter]].filter(Boolean)
      ).map(decodeURIComponent);
    });
  }

  return {
    query: decodeURIComponent(queryParameters.query || ''),
    ...filtersQueryValues,
  };
};

export function getUrlParameters() {
  const { hash, href } = location;
  const urlParts = href.match(/^(.*?)\/(jobs|locations|companies)/);
  const baseUrl = `${urlParts ? urlParts[1] : ''}/`;
  const page = urlParts?.[2] ?? '';
  const { city, state } = getCityAndStateFromUrl();

  return { baseUrl, hash, page, state, city };
}

export function routeToState(indexName, routeState = undefined, params) {
  if (!routeState) {
    routeState = getRouteStateDefaultValues(params);
  }
  const query = routeState.query;
  const filtersRouteValues = {};

  if (indexName === params['indexes']['job']) {
    params['job_filters'].forEach(filter => {
      if (jobFiltersRouteMappings[filter]) {
        filter = jobFiltersRouteMappings[filter];
      }

      filtersRouteValues[filter] = routeState[filter];
    });
  }

  return {
    [indexName]: {
      ...createNewUiState({
        query,
        configure: routeState.configure,
      }),
      ...(Object.keys(filtersRouteValues)
        ? {
          refinementList: {
            ...filtersRouteValues,
          },
        }
        : {}),
    },
  };
}

export function getRouteStateDefaultValues(params) {
  const filtersRouteStateDefaultValues = {};
  const { page } = getUrlParameters();

  if (page !== 'companies') {
    params['job_filters'].forEach(filter => {
      if (jobFiltersRouteMappings[filter]) {
        filter = jobFiltersRouteMappings[filter];
      }

      filtersRouteStateDefaultValues[filter] = undefined;
    });
  }

  return {
    query: '',
    ...filtersRouteStateDefaultValues,
  };
}

export const slugify = str =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const shuffle = array => {
  for (let currentIndex = array.length - 1; currentIndex > 0; currentIndex--) {
    let randomIndex = Math.floor(Math.random() * (currentIndex + 1));
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }
};

export const getMediaQuery = breakpoint => {
  switch (breakpoint) {
    case '2xl':
      return '(min-width: 1536px)';
    case 'xl':
      return '(min-width: 1280px)';
    case 'lg':
      return '(min-width: 1024px)';
    case 'md':
      return '(min-width: 768px)';
    default:
      return '(min-width: 640px)';
  }
};

export const mediaQuerySizes = ['sm', 'md', 'lg', 'xl', '2xl'];

export function addOrRemoveHiddenClassToAutoCompletePanel(type = 'remove') {
  const autocompletePanel = document.querySelector('.autocomplete-panel');
  if (autocompletePanel) {
    autocompletePanel.classList.toggle('hidden', type === 'add');
  }
}

export async function getDataUsingPostalCode(query) {
  let filters = `${softDeletedFilter}`;
  filters += ` AND (code = ${query})`;
  const index = getIndex('postal');
  const response = await searchClient.search([
    {
      indexName: index,
      params: {
        query: '',
        filters,
        attributesToHighlight: ['city_state', 'code', 'state'],
        attributesToRetrieve: ['city_state', 'code', 'state', '_geo'],
        highlightPostTag: '__/aa-highlight__',
        highlightPreTag: '__aa-highlight__',
        hitsPerPage: 1,
      },
    },
  ]);
  return response.results[0].hits;
}

export function storeIndexes(indexes) {
  sessionStorage.setItem('meilisearch_indexes', JSON.stringify(indexes));
}

export function getIndex(key) {
  const indexes = JSON.parse(sessionStorage.getItem('meilisearch_indexes'));
  return indexes ? indexes[key] : '';
}
